import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createInstance,
  deleteInstance,
  listAdvertiserUpdates,
  listInstanceAdvertisers,
  listInstances,
  modifyAdvertisers,
  updateInstance
} from '../amc-instances-api';
import {
  AdvertiserInput,
  AmcInstance,
  InstanceAdvertiser,
  InstanceInput
} from '../amc-instances-types';

const INSTANCES_KEY = ['amcInstances'];
const advertisersKey = (instanceId: string) => ['amcInstanceAdvertisers', instanceId];
const updatesKey = (instanceId: string) => ['amcAdvertiserUpdates', instanceId];

export const useAmcInstances = (): ReturnType<typeof useQuery<AmcInstance[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: INSTANCES_KEY,
    queryFn: listInstances
  });

export const useInstanceAdvertisers = (
  instanceId: string
): ReturnType<typeof useQuery<InstanceAdvertiser[]>> =>
  useQuery({
    queryKey: advertisersKey(instanceId),
    queryFn: () => listInstanceAdvertisers(instanceId)
  });

export const useAdvertiserUpdates = (instanceId: string) =>
  useQuery({
    queryKey: updatesKey(instanceId),
    queryFn: () => listAdvertiserUpdates(instanceId)
  });

// Mutations only invalidate after a live write; a dry run changes nothing on Amazon.

export const useCreateInstance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, dryRun }: { input: InstanceInput; dryRun: boolean }) =>
      createInstance(input, dryRun),
    onSuccess: (_result, { dryRun }) => {
      if (!dryRun) {
        queryClient.invalidateQueries({ queryKey: INSTANCES_KEY });
      }
    }
  });
};

export const useUpdateInstance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      instanceId,
      input,
      dryRun
    }: {
      instanceId: string;
      input: Omit<InstanceInput, 'instanceName'>;
      dryRun: boolean;
    }) => updateInstance(instanceId, input, dryRun),
    onSuccess: (_result, { dryRun }) => {
      if (!dryRun) {
        queryClient.invalidateQueries({ queryKey: INSTANCES_KEY });
      }
    }
  });
};

export const useDeleteInstance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ instanceId, dryRun }: { instanceId: string; dryRun: boolean }) =>
      deleteInstance(instanceId, dryRun),
    onSuccess: (_result, { dryRun }) => {
      if (!dryRun) {
        queryClient.invalidateQueries({ queryKey: INSTANCES_KEY });
      }
    }
  });
};

export const useModifyAdvertisers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      instanceId,
      advertisersToAdd,
      dryRun
    }: {
      instanceId: string;
      advertisersToAdd: AdvertiserInput[];
      dryRun: boolean;
    }) => modifyAdvertisers(instanceId, { advertisersToAdd }, dryRun),
    onSuccess: (_result, { instanceId, dryRun }) => {
      if (dryRun) {
        return;
      }
      // Adds are async, so only the pending-updates list changes right away.
      queryClient.invalidateQueries({ queryKey: updatesKey(instanceId) });
    }
  });
};
